/**
 * Copyright (c) 2010-2023 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';

import domtoimage from 'dom-to-image';
import JsPdf, { jsPDFOptions } from 'jspdf';
import { Map as OLMap } from 'ol';
import Collection from 'ol/Collection';
import { Extent } from 'ol/extent';
import { Image as ImageLayer, Layer } from 'ol/layer';
import RenderEvent from 'ol/render/Event';
import { Size } from 'ol/size';
import { ImageWMS, Source, Vector } from 'ol/source';

import { BehaviorSubject, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, delay, first, mergeMap, take, tap } from 'rxjs/operators';
import { AppConfigState } from '@core/state/appConfig/app-config.state';
import { GoogleBaseLayerConfig } from '@app/types/Google';

import { PrintConfigurationModel } from '@app/types/Print';
import { DashGisUtil } from '@shared/dash-gis-util';
import { LoggerService } from '@shared/services/logger.service';
import { RestService } from '@shared/services/rest.service';
import { MapConfigService } from '@map/services/map-config.service';
import { MapService } from '@map/services/map.service';
import { ActiveMapState } from '@map/state/active-map.state';
import { MapPrintOptions, PdfPrintResult, RenderPdfConfig } from './state/print-options.model';
import Timeout = NodeJS.Timeout;

@Injectable()
export class PrintService {
    public paperOptions: any[];

    private progressSubject = new BehaviorSubject<number>(0);

    private sourcesLoaded: any[];
    private printMap: OLMap = null;

    private googleApiKey: string;
    private printConfig: any;
    private printResolution: number;
    private printCanvas: HTMLCanvasElement;
    private printSettings: MapPrintOptions;
    private titleMargin = 8;
    private readonly maxPrintSize: 2048;

    private printConfig$: Observable<PrintConfigurationModel> = inject(Store).select(AppConfigState.print);
    private renderTimeout: Timeout;
    private readonly debug = false;

    /**
     * @param logger {LoggerService}
     * @param rest {RestService}
     * @param mapConfig {MapConfigService}
     * @param mapService {MapService}
     * @param store {Store}
     */
    constructor(
        private logger: LoggerService,
        private rest: RestService,
        private mapConfig: MapConfigService,
        private mapService: MapService,
        private store: Store
    ) {
        this.googleApiKey = null;
        this.printSettings = {};
        this.printConfig$.subscribe((config) => {
            this.printConfig = config;
            this.paperOptions = this.printConfig.paper.map((item) => ({
                label: item.label,
                value: item,
            }));
            this.printSettings.paperSize = this.paperOptions[0].value;
        });

        const baseLayer = this.mapConfig.getBaseLayer();
        if (baseLayer.type === 'google') {
            this.googleApiKey = (baseLayer as GoogleBaseLayerConfig).apiKey;
        }
    }

    public pdfPrint(options: MapPrintOptions): Observable<any> {
        const printSubject = new Subject<PdfPrintResult>();
        const printConfig = this.store.selectSnapshot(AppConfigState.print);
        const paperConfig = printConfig.paper.find((p) => p.name === options.paperSize.value.name);
        const paperSize = paperConfig.size;

        this.printResolution = options.highQuality ? this.printConfig.highResolution : this.printConfig.lowResolution;

        const legendBorder = 0;
        const legendWidth = options.showLegend ? paperConfig.legend.width : 0;

        const outerLegendWidth = options.showLegend ? legendWidth + paperConfig.legend.margin + 2 * legendBorder : 0;

        const logoElement = window.document.getElementById('menu-bar-image') as HTMLImageElement;

        const titleHeight = options.showTitle || logoElement ? paperConfig.title.height + this.titleMargin : 0;
        const disclaimerHeight = printConfig?.disclaimer ? paperConfig.disclaimer.height : 0;

        const mapPdfWidth = paperSize.width - paperConfig.margin.left - paperConfig.margin.right - outerLegendWidth;
        let mapWidth = Math.round(mapPdfWidth * this.printResolution);

        const mapPdfHeight =
            paperSize.height - paperConfig.margin.top - titleHeight - disclaimerHeight - paperConfig.margin.bottom;
        let mapHeight = Math.round(mapPdfHeight * this.printResolution);

        [mapWidth, mapHeight] = this.scaleToMaxSize(mapWidth, mapHeight, this.maxPrintSize);

        this.printMap = this.mapService.createPrintMap('printMap');
        this.printMap.setSize([mapWidth, mapHeight]);

        // make map DOM element the same size
        const mapElement: HTMLElement = document.querySelector('#printMap');
        mapElement.style.width = `${mapWidth}px`;
        mapElement.style.height = `${mapHeight}px`;

        // force zoom to integer
        const constrainedZoom = Math.round(this.mapService.map.getView().getZoom());
        this.printMap.getView().setZoom(constrainedZoom);

        this.printMap.once('precompose', (_: RenderEvent) => {
            this.progressSubject.next(20);
            this.sourcesLoaded = [];
            const layers = this.printMap.getLayers() as Collection<Layer>;
            const layerProgressIncrement = 30 / layers.getLength();
            const renderConfig: RenderPdfConfig = {
                data: null,
                printSubject,
                mapPdfHeight,
                mapPdfWidth,
                options,
                outerLegendWidth,
                paperConfig,
                logoElement,
                disclaimer: printConfig?.disclaimer,
            };
            layers.forEach((layer: Layer) => {
                layer.once('prerender', (layer_event: RenderEvent) => {
                    if (layer instanceof ImageLayer && !this.printCanvas) {
                        this.printCanvas = layer_event.context.canvas as HTMLCanvasElement;
                    }
                    const source: Source = layer.getSource();

                    // set a unique id on the source so we can match the event
                    // target to it later
                    const tempId = 'id-' + DashGisUtil.uid();
                    source.set('uid', tempId);
                    const sourceInfo = {
                        listenerKeys: [],
                        loaded: source instanceof Vector,
                        tileCount: 0,
                        uid: tempId,
                    };
                    if (source instanceof ImageWMS) {
                        const key = source.once(['imageloadend'], (e) => {
                            this.sourcesLoaded.find((item) => item.uid === e.target.get('uid')).loaded = true;
                            this.sourceLoaded(renderConfig);
                            this.progressSubject.next(this.progressSubject.getValue() + layerProgressIncrement);
                        });
                        sourceInfo.tileCount = 1;
                        sourceInfo.listenerKeys.push(key);
                        source.setImageLoadFunction(source.getImageLoadFunction());
                    }
                    this.sourcesLoaded.push(sourceInfo);
                });
            });
        });
        this.printMap.setSize([mapWidth, mapHeight]);
        this.printMap.getView().setCenter(this.mapService.center);
        return printSubject.asObservable();
    }

    public cancelPrint() {
        if (this.renderTimeout) {
            clearTimeout(this.renderTimeout);
            this.renderTimeout = null;
        }
        if (this.printMap) {
            this.printMap.setTarget(null);
            this.printMap = null;
        }
    }

    private sourceLoaded(renderConfig) {
        if (this.renderTimeout) {
            clearTimeout(this.renderTimeout);
        }
        this.renderTimeout = setTimeout(() => {
            const allSourcesLoaded = this.sourcesLoaded.every((item) => item.loaded);
            if (!allSourcesLoaded) {
                return;
            }

            const renderStreams: Observable<void>[] = [];
            this.printMap.getOverlays().forEach((overlay) => {
                renderStreams.push(this.renderToCanvas(overlay.getElement(), this.printCanvas));
            });

            if (renderStreams.length === 0) {
                renderStreams.push(of(null));
            }

            forkJoin(renderStreams)
                .pipe(concatMap(() => this.renderGoogleMap()))
                .subscribe(() => {
                    this.renderMapToPdf(renderConfig);
                });
        }, 0);
    }

    private renderGoogleMap(): Observable<void> {
        // get google static map: OL3 clears the context as part of the render process,
        // so loading the google map must happen AFTER the other layers are rendered.

        // pass center, zoom, size, type
        const googleBaseUrl = 'https://maps.googleapis.com/maps/api/staticmap';

        const [googleWidth, googleHeight] = this.scaleToMaxSize(
            this.printCanvas.width,
            this.printCanvas.height,
            this.maxPrintSize
        );
        const latLon = this.mapService.centerLatLon;
        const googleLonLat = [latLon[1], latLon[0]];
        const googleMapType = this.store.selectSnapshot(ActiveMapState.googleLayerType);
        const params = [
            'center=' + encodeURIComponent(googleLonLat.join(',')),
            'zoom=' + encodeURIComponent(this.printMap.getView().getZoom().toString()),
            'size=' + encodeURIComponent(`${googleWidth}x${googleHeight}`),
            'format=png32',
            `maptype=${googleMapType}`,
            `key=${this.googleApiKey}`,
        ].join('&');
        const googleImage = new Image();
        googleImage.setAttribute('crossorigin', 'anonymous');
        const apiUrl = `${googleBaseUrl}?${params}`;
        const signUrl = btoa(apiUrl).replace('+', '-').replace('/', '_');

        return this.rest.apiGet('map/sign', { url: signUrl }, { responseType: 'text' }).pipe(
            concatMap((signature) => {
                const drawSubject = new Subject<void>();

                googleImage.src = `${apiUrl}&signature=${signature}`;
                googleImage.onload = () => {
                    const ctx = this.printCanvas.getContext('2d');
                    const originalCompositeValue = ctx.globalCompositeOperation;
                    ctx.globalCompositeOperation = 'destination-over';
                    ctx.drawImage(
                        googleImage,
                        0,
                        0,
                        googleWidth,
                        googleHeight,
                        0, // (ctx.canvas.width - googleWidth) / 2,
                        0, // (ctx.canvas.height - googleHeight) / 2,
                        googleWidth * window.devicePixelRatio,
                        googleHeight * window.devicePixelRatio
                    );
                    ctx.globalCompositeOperation = originalCompositeValue;
                    drawSubject.next();
                    drawSubject.complete();
                };
                return drawSubject.asObservable();
            })
        );
    }

    //    renderMapToPdf(deferred, data, paperConfig, outerLegendWidth, titleHeight, mapPdfWidth, mapPdfHeight,
    // options) {
    private renderMapToPdf(renderConfig: RenderPdfConfig) {
        this.progressSubject.next(60);
        const streams = [];
        const canvas = this.printCanvas;
        renderConfig.data = canvas.toDataURL('image/jpeg', 0.85);
        const pdfOptions: jsPDFOptions = {
            orientation: 'landscape',
            unit: 'pt',
            format: renderConfig.paperConfig.name,
        };
        const pdf = new JsPdf(pdfOptions);
        const mapX = renderConfig.paperConfig.margin.left + renderConfig.outerLegendWidth;
        const mapY =
            renderConfig.paperConfig.margin.top +
            renderConfig.paperConfig.title.height +
            (renderConfig.paperConfig.title.height ? this.titleMargin : 0);
        pdf.addImage(renderConfig.data, 'JPEG', mapX, mapY, renderConfig.mapPdfWidth, renderConfig.mapPdfHeight);

        if (renderConfig.options.showTitle) {
            this.createTitle(pdf, renderConfig);
        }

        if (this.printConfig?.disclaimer) {
            this.createDisclaimer(pdf, renderConfig);
        }

        if (renderConfig.logoElement) {
            const pdfWidth = renderConfig.paperConfig.size.width;
            const logoScale = renderConfig.paperConfig.title.height / renderConfig.logoElement.naturalHeight;
            const logoWidth = renderConfig.logoElement.naturalWidth * logoScale;
            const logoHeight = renderConfig.logoElement.naturalHeight * logoScale;
            const logoX = pdfWidth - renderConfig.paperConfig.margin.right - logoWidth;
            const logoY = renderConfig.paperConfig.margin.top;
            pdf.addImage(renderConfig.logoElement, null, logoX, logoY, logoWidth, logoHeight);
        }

        if (renderConfig.options.showNorth) {
            this.createNorthArrow(
                pdf,
                renderConfig.paperConfig.size.width - renderConfig.paperConfig.margin.right - 50 - 10,
                renderConfig.paperConfig.margin.top + renderConfig.paperConfig.title.height + 10,
                50,
                50
            );
        }

        if (renderConfig.options.showLegend) {
            this.progressSubject.next(80);
            const legend$ = this.createPrintLegend(
                pdf,
                renderConfig.paperConfig,
                renderConfig.paperConfig.title.height
            ).pipe(
                tap(() => this.progressSubject.next(90)),
                mergeMap(() => of('legend'))
            );
            streams.push(legend$);
        }

        // if there are no observables to wait on, add something to subscribe to
        if (streams.length === 0) {
            streams.push(of(null));
        }

        forkJoin(streams)
            .pipe(
                tap(() => {
                    if (this.debug) {
                        this.debugPdf(pdf, renderConfig);
                    }
                    this.progressSubject.next(100);
                    this.outputPdf(renderConfig, pdf);
                })
            )
            .subscribe();
    }

    private outputPdf(renderConfig: RenderPdfConfig, pdf) {
        let pdfBlob;
        let isBlob = false;
        const supportsBlob = !!new Blob();
        if (supportsBlob) {
            pdfBlob = pdf.output('blob');
            isBlob = true;
        } else {
            pdfBlob = pdf.output('bloburi');
        }

        pdf = null;
        this.printMap.setTarget(null);
        this.printMap = null;

        const filename = (renderConfig.options.titleText || this.mapConfig.title)
            .replace(/ /g, '_')
            .toLocaleLowerCase()
            .concat('.pdf');
        renderConfig.printSubject.next({
            blob: pdfBlob,
            isBlob,
            filename,
        });
    }

    private createTitle(pdf: JsPdf, renderConfig: RenderPdfConfig) {
        const ml = renderConfig.paperConfig.margin.left;
        const mt = renderConfig.paperConfig.margin.top;
        const th = renderConfig.paperConfig.title.height;
        const font = renderConfig.paperConfig.title.font;
        const fontStyle = renderConfig.paperConfig.title.fontStyle;
        const fontSize = renderConfig.paperConfig.title.fontSize;

        pdf.setDrawColor(0, 0, 0)
            .setFont(font ?? 'helvetica', fontStyle ?? 'normal')
            .setFontSize(fontSize ?? 16)
            .text(renderConfig.options.titleText, ml, mt, {
                baseline: 'top',
                lineHeightFactor: 1.0,
            });
    }

    private createDisclaimer(pdf: JsPdf, renderConfig: RenderPdfConfig) {
        const ml = renderConfig.paperConfig.margin.left;
        const mr = renderConfig.paperConfig.margin.right;
        const mb = renderConfig.paperConfig.margin.bottom;
        const height = renderConfig.paperConfig.size.height;
        const width = renderConfig.paperConfig.size.width;
        const legendWidth = renderConfig.outerLegendWidth;
        const x = ml + legendWidth + renderConfig.mapPdfWidth / 2;
        const dh = height - mb - renderConfig.paperConfig.disclaimer.height;
        const font = renderConfig.paperConfig.disclaimer.font;
        const fontStyle = renderConfig.paperConfig.disclaimer.fontStyle;
        const fontSize = renderConfig.paperConfig.disclaimer.fontSize;

        pdf.setDrawColor('0')
            .setFont(font || 'helvetica', fontStyle ?? 'normal')
            .setFontSize(fontSize ?? 16)
            .text(renderConfig.disclaimer, x, dh, {
                baseline: 'top',
                align: 'center',
                maxWidth: width - ml - mr - legendWidth - 30,
                lineHeightFactor: 1.25,
            });
    }

    /**
     * Scale up to but not exceeding max size, maintaining the existing aspect ratio.
     * @param width {number}
     * @param height {number}
     * @param maxDimension {number}
     * @returns {[*,*]}
     */
    private scaleToMaxSize(width, height, maxDimension = 2048) {
        if (width <= 0 || height <= 0) {
            throw new RangeError('width and height arguments must be greater than zero.');
        }

        // canvas width,height have already been scaled by devicePixelRatio
        width = width / window.devicePixelRatio;
        height = height / window.devicePixelRatio;
        let scaledWidth;
        let scaledHeight;
        if (maxDimension >= width && maxDimension >= height) {
            [scaledWidth, scaledHeight] = [width, height];
        } else {
            const ratio = maxDimension / Math.max(width, height);
            [scaledWidth, scaledHeight] = [Math.ceil(width * ratio), Math.ceil(height * ratio)];
        }
        return [Math.floor(scaledWidth), Math.floor(scaledHeight)];
    }

    /**
     * Draws the legend in the given jsPDF object.
     * Configuration parameters in paperConfig (in points):
     * - legend.width: Width of the legend box. The box surrounding is a 1pt rule centered on
     * the rectangle edge
     * - legend.padding: Width of the space inside the containing rectangle
     * - legend.margin: Space between the legend container and the map image. This is in addition to the
     * size given in legend.width
     * @param pdf {JsPdf}
     * @param paperConfig {Object}
     * @param titleHeight {int}
     * @returns {Observable<void>}
     */
    private createPrintLegend(pdf: JsPdf, paperConfig, titleHeight): Observable<any> {
        // units are those specified in pdf object creation (points)
        // text y value is the position of the _baseline_
        const borderWidth = 0;
        const top = paperConfig.margin.top + titleHeight + (titleHeight ? this.titleMargin : 0);
        const left = paperConfig.margin.left;
        const contentLeft = left + paperConfig.legend.padding + borderWidth;
        const contentWidth = paperConfig.legend.width - 2 * paperConfig.legend.padding - 2 * borderWidth;

        const indent = 9;
        const headerTextSize = 12;
        const layerTextSize = 9;
        let vPos = top + paperConfig.legend.padding;
        let hPos = left + paperConfig.legend.padding;
        const scale = this.mapService.currentScale;
        const iconUnitWidth = 18;
        const iconSpace = 4;
        const iconWidth = Math.round(iconUnitWidth * this.printResolution);
        const iconHeight = Math.round(layerTextSize * this.printResolution);

        const addText = (size, x, y, lines) => {
            // adjust the text baseline so it sits higher than the bottom of the image rect
            // getting the actual font metrics is a pain, so this is based on guessing :)
            const baselineOffset = (size * 0.3) / 2.0;
            pdf.setFontSize(size);
            pdf.text(lines, x, y + size - baselineOffset, {
                baseline: 'bottom',
                lineHeightFactor: 1.0,
            });
            return y + size * lines.length;
        };

        const currentFont = pdf.getFont();
        pdf.setFont('BarlowSemiCondensed', 'Bold');
        pdf.setFontSize(headerTextSize);
        pdf.text('Legend', hPos, vPos + headerTextSize, { baseline: 'bottom', lineHeightFactor: 1.2 });
        vPos += headerTextSize;
        pdf.setFont(currentFont.fontName, currentFont.fontStyle);

        // loop through layers and classes and collect data. When the icon request promises resolve,
        // loop back through and insert into the pdf
        const legendData = [];
        const icons$: Observable<any>[] = [];

        const printLayers = this.mapConfig.getVisibleLayerList(this.mapService.currentScale);

        for (const layer of printLayers) {
            // classes
            const layerClasses = [];
            for (let j = 0; j < layer.classes.length; j++) {
                const layerClass = layer.classes[j];
                if (layerClass.minScale <= scale && scale <= layerClass.maxScale) {
                    const classData = {
                        icon: null,
                        name: layerClass.name,
                    };
                    // class legend icon
                    const options = {
                        layer,
                        classIndex: j,
                        iconWidth,
                        iconHeight,
                    };
                    const icon$ = this.loadClassIcon(options).pipe(
                        tap((iconData) => {
                            classData.icon = iconData;
                        })
                    );
                    icons$.push(icon$);
                    layerClasses.push(classData);
                }
            }
            legendData.push({
                classes: layerClasses,
                label: layer.label,
            });
        }
        return forkJoin(icons$).pipe(
            first(),
            tap(() => {
                for (const layerData of legendData) {
                    hPos = contentLeft;

                    // layer name
                    const layerNameLines = pdf.splitTextToSize(layerData.label, contentWidth);
                    vPos += 4;
                    vPos = addText(layerTextSize, hPos, vPos, layerNameLines);

                    // layer separator rule
                    vPos += 4;
                    pdf.setLineWidth(1).line(hPos, vPos, hPos + contentWidth, vPos);
                    vPos += 4;

                    // classes
                    for (const layerClass of layerData.classes) {
                        hPos = left + indent;
                        // class legend icon
                        pdf.addImage(layerClass.icon, 'PNG', hPos, vPos, iconUnitWidth, layerTextSize);
                        //pdf.setLineWidth(0.75).rect(hPos, vPos, iconUnitWidth, layerTextSize);
                        hPos += iconUnitWidth + iconSpace;

                        // class name
                        // check width
                        const classNameLines = pdf.splitTextToSize(
                            layerClass.name.toString(),
                            contentWidth - (indent + iconUnitWidth + iconSpace)
                        );
                        vPos = addText(layerTextSize, hPos, vPos, classNameLines);
                        vPos += 2;
                    }
                    vPos += 4;
                }
                // pdf.setDrawColor('0')
                //     .setLineWidth(1)
                //     .rect(left, top, paperConfig.legend.width - paperConfig.legend.margin, vPos - top, 'S');
            })
        );
    }

    private loadClassIcon({ layer, classIndex, iconWidth, iconHeight }): Observable<string> {
        const url = `map/icon/${this.mapConfig.mapName}/${layer.name}/${classIndex}/${iconWidth}/${iconHeight}`;
        return this.rest.apiGet<string>(url, null).pipe(
            delay(10),
            catchError((err, caught) => {
                console.log(err);
                return caught;
            }),
            take(1)
        );
    }

    private createNorthArrow(pdf: JsPdf, x: number, y: number, w: number, h: number) {
        const tX = w / 100.0;
        const tY = h / 100.0;
        const scaleFactor = [tX, tY];
        const drawColor = '0.25';
        pdf.setFillColor('1').ellipse(tX * 50 + x, tY * 50 + y, tX * 49.1, tY * 49.1, 'F');
        pdf.setDrawColor(drawColor)
            .setLineWidth(3)
            .ellipse(tX * 50 + x, tY * 50 + y, tX * 39.1, tY * 39.1, 'S');
        pdf.setFillColor('1').rect(tX * 41 + x, tY * 6.8 + y, tX * 18, tY * 9, 'F');
        pdf.setFillColor(drawColor).lines(
            [
                [-5.6, 0],
                [0, -3.3],
                [1.2, 0],
                [0, -7],
                [-1.2, 0],
                [0, -3.3],
                [6.4, 0],
                [4.9, 7.1],
                [0, -3.8],
                [-1.1, 0],
                [0, -3.3],
                [5.5, 0],
                [0, 3.3],
                [-1.2, 0],
                [0, 10.5],
                [-4.5, 0],
                [-5.7, -8.2],
                [0, 4.8],
                [1.2, 0],
                [0, 3.2],
            ],
            tX * 47.9 + x,
            tY * 17.6 + y,
            scaleFactor,
            'F',
            true
        );
        pdf.lines(
            [
                [-24, -5],
                [6, -11],
                [-11, 6],
                [-5, -24],
                [-5, 24],
                [-11, -6],
                [6, 11],
                [-24, 5],
                [24, 5],
                [-6, 11],
                [11, -6],
                [5, 24],
                [5, -24],
                [11, 6],
                [-6, -11],
            ],
            tX * 84 + x,
            tY * 50 + y,
            scaleFactor,
            'F',
            true
        );
        pdf.setFillColor('1').lines(
            [
                [-2, 8],
                [-2, -8],
                [-8, -2],
                [8, -2],
                [2, -8],
                [2, 8],
                [8, 2],
            ],
            tX * 52 + x,
            tY * 52 + y,
            scaleFactor,
            'F',
            true
        );
    }

    private createScaleBar(pdf: JsPdf) {
        const size: Size = this.printMap.getSize();
        const extent: Extent = this.printMap.getView().calculateExtent(size);
        const config = {
            params: {
                height: size[1],
                maxx: extent[2],
                maxy: extent[3],
                minx: extent[0],
                miny: extent[1],
                width: size[0],
            },
        };
        return this.rest.apiGet<string>(`/map/scalebar/${this.mapConfig.mapName}`, config).subscribe((imageData) => {
            // load the image into an element to get the size, which will vary depending on scale, etc.
            const image = new Image();
            image.onload = () => {
                const imageWidth = image.width;
                const imageHeight = image.height;
                pdf.addImage(imageData, 'PNG', 200, 200, imageWidth, imageHeight);
            };
            image.src = imageData;
        });
    }

    private renderToCanvas(element: HTMLElement, canvas: HTMLCanvasElement) {
        // overlay elements are positioned via parent absolute positioning, so offsetLeft
        // and offsetTop on the displayed element will be zero. So, we get only those values
        // from the parent
        const x = (element.offsetParent as HTMLElement).offsetLeft * window.devicePixelRatio;
        const y = (element.offsetParent as HTMLElement).offsetTop * window.devicePixelRatio;

        const returnSubject = new Subject<void>();

        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.onload = () => {
            const originalCompositeValue = ctx.globalCompositeOperation;
            // ctx.globalCompositeOperation = 'copy';
            ctx.drawImage(img, x, y);
            ctx.globalCompositeOperation = originalCompositeValue;
            returnSubject.next();
            returnSubject.complete();
        };
        domtoimage.toPng(element).then((dataUrl) => {
            img.src = dataUrl;
        });
        return returnSubject.asObservable();
    }

    private debugPdf(pdf: JsPdf, renderConfig: RenderPdfConfig) {
        const ml = renderConfig.paperConfig.margin.left;
        const mr = renderConfig.paperConfig.margin.right;
        const mt = renderConfig.paperConfig.margin.top;
        const mb = renderConfig.paperConfig.margin.bottom;
        const width = renderConfig.paperConfig.size.width;
        const height = renderConfig.paperConfig.size.height;
        const disHeight = height - mb - renderConfig.paperConfig.disclaimer.height;
        const th = renderConfig.paperConfig.title.height;

        // draw margin box
        pdf.setDrawColor(255, 0, 0).rect(ml, mt, width - ml - mr, height - mt - mb);
        // draw title area
        pdf.setDrawColor(0, 0, 255).rect(ml, mt, width - ml - mr, th);
        //
        pdf.setDrawColor(0, 0, 255).rect(ml, mt, width - ml - mr, th);
        // draw disclaimer top
        pdf.setDrawColor(255, 0, 128).line(0, disHeight, width, disHeight);
    }

    // region property accessors
    get progress$() {
        return this.progressSubject.asObservable();
    }

    // endregion
}
